import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, FileText, Clock, CheckCircle, AlertCircle, CreditCard, X, ExternalLink, Edit2, Trash2, Palette, Download, FileCode, Eye, Send, Square, RefreshCw, Plus } from 'lucide-react';
import { formatDate as formatDateUtil } from '../utils/dateUtils';
import SpaceBackground from '../components/layout/SpaceBackground';
import CreateInvoiceModal from '../components/invoices/CreateInvoiceModal';

// Invoices module v2
const API_URL = process.env.REACT_APP_API_URL || '/api';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const pageSize = 50;
  const navigate = useNavigate();

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('check');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editDueDate, setEditDueDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editDiscount, setEditDiscount] = useState('');
  const [submittingEdit, setSubmittingEdit] = useState(false);

  // LEDES export modal state
  const [showLedesModal, setShowLedesModal] = useState(false);
  const [ledesInvoice, setLedesInvoice] = useState(null);
  const [ledesFormat, setLedesFormat] = useState('ledes_1998b');
  const [exporting, setExporting] = useState(false);

  // Preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Send via Square state
  const [sendingSquare, setSendingSquare] = useState(null);

  // Create invoice modal state
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchStats();
  }, [currentPage, filterStatus]);

  const fetchInvoices = async () => {
    console.log('Fetching invoices from:', `${API_URL}/invoices/`);
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);

      const params = {
        page: currentPage,
        page_size: pageSize
      };

      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const response = await axios.get(`${API_URL}/invoices/`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      setInvoices(response.data.invoices || []);
      setTotalInvoices(response.data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoices/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-virgo-navy-800 text-virgo-teal-100',
      sent: 'bg-blue-100 text-blue-800',
      viewed: 'bg-indigo-100 text-indigo-800',
      partial: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-virgo-navy-800 text-virgo-teal-100'
    };
    return colors[status] || 'bg-virgo-navy-800 text-virgo-teal-100';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return formatDateUtil(dateString);
  };

  const openPaymentModal = (invoice, e) => {
    e.stopPropagation();
    setSelectedInvoice(invoice);
    setPaymentAmount(invoice.amount_due.toString());
    setPaymentMethod('check');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setReferenceNumber('');
    setPaymentNotes('');
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedInvoice(null);
    setEditingPayment(null);
    setPaymentAmount('');
    setReferenceNumber('');
    setPaymentNotes('');
  };

  const refreshPreview = async (invoiceId) => {
    if (!previewInvoice || previewInvoice.id !== invoiceId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewInvoice(response.data);
    } catch (err) {
      console.error('Error refreshing preview:', err);
    }
  };

  const openEditPaymentModal = (payment) => {
    setSelectedInvoice(previewInvoice);
    setEditingPayment(payment);
    setPaymentAmount(payment.amount.toString());
    setPaymentMethod(payment.payment_method);
    setPaymentDate((payment.payment_date || '').split('T')[0]);
    setReferenceNumber(payment.reference_number || '');
    setPaymentNotes(payment.notes || '');
    setShowPaymentModal(true);
  };

  const handleVoidPayment = async (payment) => {
    const reason = window.prompt('Void this payment? Enter a reason (kept in the audit trail):');
    if (reason === null) return;
    if (!reason.trim()) { alert('A reason is required to void a payment.'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/invoices/${previewInvoice.id}/payments/${payment.id}/void`,
        { reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchInvoices();
      fetchStats();
      await refreshPreview(previewInvoice.id);
      alert('Payment voided.');
    } catch (err) {
      console.error('Error voiding payment:', err);
      alert('Failed to void payment: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteInvoice = async (invoice, e) => {
    e.stopPropagation();

    if (!window.confirm(`Are you sure you want to delete invoice ${invoice.invoice_number}? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/invoices/${invoice.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchInvoices();
      fetchStats();
    } catch (err) {
      console.error('Error deleting invoice:', err);
      alert('Failed to delete invoice: ' + (err.response?.data?.detail || err.message));
    }
  };

  const openEditModal = (invoice, e) => {
    e.stopPropagation();
    setEditInvoice(invoice);
    setEditDueDate(invoice.due_date ? invoice.due_date.split('T')[0] : '');
    setEditNotes(invoice.notes || '');
    setEditDiscount(invoice.discount_amount?.toString() || '0');
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditInvoice(null);
    setEditDueDate('');
    setEditNotes('');
    setEditDiscount('');
  };

  const handleEditInvoice = async (e) => {
    e.preventDefault();

    if (!editInvoice) return;

    try {
      setSubmittingEdit(true);
      const token = localStorage.getItem('token');

      const updateData = {};
      if (editDueDate) updateData.due_date = editDueDate;
      if (editNotes !== editInvoice.notes) updateData.notes = editNotes;
      if (editDiscount !== editInvoice.discount_amount?.toString()) {
        updateData.discount_amount = parseFloat(editDiscount) || 0;
      }

      await axios.put(
        `${API_URL}/invoices/${editInvoice.id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      closeEditModal();
      fetchInvoices();
      fetchStats();
    } catch (err) {
      console.error('Error updating invoice:', err);
      alert('Failed to update invoice: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmittingEdit(false);
    }
  };

  // LEDES Export functions
  const openLedesModal = (invoice, e) => {
    e.stopPropagation();
    setLedesInvoice(invoice);
    setLedesFormat('ledes_1998b');
    setShowLedesModal(true);
  };

  const closeLedesModal = () => {
    setShowLedesModal(false);
    setLedesInvoice(null);
  };

  const handleLedesExport = async () => {
    if (!ledesInvoice) return;

    try {
      setExporting(true);
      const token = localStorage.getItem('token');

      const response = await axios.post(
        `${API_URL}/invoices/${ledesInvoice.id}/ledes-export`,
        { format: ledesFormat },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const blob = new Blob([response.data], {
        type: ledesFormat === 'ledes_2000' ? 'application/xml' : 'text/plain'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${ledesInvoice.invoice_number}_${ledesFormat}.${ledesFormat === 'ledes_2000' ? 'xml' : 'txt'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      closeLedesModal();
    } catch (err) {
      console.error('Error exporting LEDES:', err);
      alert('Failed to export LEDES: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Preview invoice functions
  const openPreviewModal = async (invoice, e) => {
    e.stopPropagation();
    setLoadingPreview(true);
    setShowPreviewModal(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/invoices/${invoice.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewInvoice(response.data);
    } catch (err) {
      console.error('Error fetching invoice details:', err);
      alert('Failed to load invoice details');
      setShowPreviewModal(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewInvoice(null);
  };

  // Send via Square
  const handleSendViaSquare = async (invoice, e) => {
    e.stopPropagation();

    if (!window.confirm(`Send invoice ${invoice.invoice_number} via Square?\n\nThis will email a payment link to the client.`)) {
      return;
    }

    setSendingSquare(invoice.id);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/invoices/${invoice.id}/square/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Invoice sent successfully!\n\nPayment link: ${response.data.payment_url}`);
      fetchInvoices();
      fetchStats();
    } catch (err) {
      console.error('Error sending via Square:', err);
      alert('Failed to send invoice: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingSquare(null);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();

    if (!selectedInvoice || !paymentAmount) return;

    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    try {
      setSubmittingPayment(true);
      const token = localStorage.getItem('token');

      const body = {
        amount: amount,
        payment_method: paymentMethod,
        payment_date: paymentDate,
        reference_number: referenceNumber || null,
        notes: paymentNotes || null
      };
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const invId = selectedInvoice.id;
      const wasEditing = !!editingPayment;
      if (wasEditing) {
        await axios.patch(`${API_URL}/invoices/${invId}/payments/${editingPayment.id}`, body, auth);
      } else {
        await axios.post(`${API_URL}/invoices/${invId}/payments`, body, auth);
      }

      closePaymentModal();
      fetchInvoices();
      fetchStats();
      await refreshPreview(invId);
      alert(wasEditing ? 'Payment updated.' : 'Payment recorded successfully!');
    } catch (err) {
      console.error('Error recording payment:', err);
      alert('Failed to record payment: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmittingPayment(false);
    }
  };

  const totalPages = Math.ceil(totalInvoices / pageSize);

  if (loading && invoices.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-8 relative">
      <SpaceBackground />
      <div className="relative z-10">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-virgo-teal-100">Invoices</h1>
          <p className="text-virgo-teal-300 mt-1">Manage billing and payments</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/recurring-invoices')}
            className="bg-virgo-navy-600 text-virgo-teal-100 px-4 py-2 rounded-lg hover:bg-virgo-navy-500 transition flex items-center"
          >
            <RefreshCw className="mr-2" size={18} />
            Recurring
          </button>
          <button
            onClick={() => navigate('/invoice-templates')}
            className="bg-virgo-navy-600 text-virgo-teal-100 px-4 py-2 rounded-lg hover:bg-virgo-navy-500 transition flex items-center"
          >
            <Palette className="mr-2" size={18} />
            Design Templates
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-virgo-teal-600 text-white px-6 py-2 rounded-lg hover:bg-virgo-teal-700 transition flex items-center"
          >
            <Plus className="mr-2" size={18} />
            New Invoice
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-virgo-navy-700 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-virgo-teal-300 text-sm">Total Outstanding</p>
                <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.total_outstanding)}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <Clock className="text-orange-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-virgo-navy-700 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-virgo-teal-300 text-sm">Total Paid</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.total_paid)}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-virgo-navy-700 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-virgo-teal-300 text-sm">YTD Revenue</p>
                <p className="text-2xl font-bold text-virgo-teal-400">{formatCurrency(stats.total_revenue_ytd)}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <DollarSign className="text-virgo-teal-400" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-virgo-navy-700 p-6 rounded-lg shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-virgo-teal-300 text-sm">Overdue</p>
                <p className="text-2xl font-bold text-red-600">{stats.overdue_invoices}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <AlertCircle className="text-red-600" size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-virgo-navy-700 rounded-lg shadow mb-6 p-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'all'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-200 hover:bg-gray-300'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterStatus('draft')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'draft'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-200 hover:bg-gray-300'
            }`}
          >
            Draft
          </button>
          <button
            onClick={() => setFilterStatus('sent')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'sent'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-200 hover:bg-gray-300'
            }`}
          >
            Sent
          </button>
          <button
            onClick={() => setFilterStatus('paid')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'paid'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-200 hover:bg-gray-300'
            }`}
          >
            Paid
          </button>
          <button
            onClick={() => setFilterStatus('overdue')}
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'overdue'
                ? 'bg-virgo-teal-600 text-white'
                : 'bg-virgo-navy-700 text-virgo-teal-200 hover:bg-gray-300'
            }`}
          >
            Overdue
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Invoices Table */}
      <div className="bg-virgo-navy-700 rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-virgo-navy-600">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Invoice #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Case
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Balance
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-virgo-teal-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-virgo-navy-700 divide-y divide-gray-200">
            {invoices && invoices.length > 0 ? (
              invoices.map((invoice) => (
                <tr
                  key={invoice.id}
                  className="hover:bg-virgo-navy-600 transition"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <FileText className="text-virgo-teal-500 mr-2" size={18} />
                      <span className="text-sm font-medium text-white">
                        {invoice.invoice_number}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-300">
                    {invoice.contact_name || <span className="text-virgo-teal-600">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-300 max-w-xs truncate" title={invoice.case_name || ''}>
                    {invoice.case_name || <span className="text-virgo-teal-600">—</span>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-virgo-teal-400">
                    {formatDate(invoice.invoice_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                    {formatCurrency(invoice.total_amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                    {formatCurrency(invoice.amount_due)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* View/Preview button */}
                      <button
                        onClick={(e) => openPreviewModal(invoice, e)}
                        className="p-2 bg-virgo-navy-500 text-virgo-teal-100 rounded-lg hover:bg-virgo-navy-400 transition border border-virgo-teal-700/50"
                        title="View Invoice"
                      >
                        <Eye size={16} />
                      </button>
                      {/* Edit button for draft invoices */}
                      {invoice.status === 'draft' && (
                        <button
                          onClick={(e) => openEditModal(invoice, e)}
                          className="p-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700 transition"
                          title="Edit Invoice"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                      {/* Send via Square - for draft invoices */}
                      {invoice.status === 'draft' && invoice.amount_due > 0 && (
                        <button
                          onClick={(e) => handleSendViaSquare(invoice, e)}
                          disabled={sendingSquare === invoice.id}
                          className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50"
                          title="Send via Square"
                        >
                          {sendingSquare === invoice.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          ) : (
                            <Send size={16} />
                          )}
                        </button>
                      )}
                      {/* Delete button for draft invoices */}
                      {invoice.status === 'draft' && (
                        <button
                          onClick={(e) => handleDeleteInvoice(invoice, e)}
                          className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                          title="Delete Invoice"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      {/* Record Payment */}
                      {invoice.amount_due > 0 && invoice.status !== 'draft' && invoice.status !== 'cancelled' && (
                        <button
                          onClick={(e) => openPaymentModal(invoice, e)}
                          className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                          title="Record Payment"
                        >
                          <CreditCard size={16} />
                        </button>
                      )}
                      {/* Square Payment Link */}
                      {invoice.square_payment_url && (
                        <a
                          href={invoice.square_payment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition inline-block"
                          title="Open Payment Link"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                      {/* LEDES Export */}
                      {(invoice.status === 'sent' || invoice.status === 'paid' || invoice.status === 'partial') && (
                        <button
                          onClick={(e) => openLedesModal(invoice, e)}
                          className="p-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition"
                          title="Export LEDES"
                        >
                          <FileCode size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center">
                  <p className="text-virgo-teal-400 text-lg">No invoices found</p>
                  <p className="text-virgo-teal-500 text-sm mt-2">Sync from Clio or create your first invoice to get started</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-600"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-virgo-teal-200">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-virgo-navy-600"
          >
            Next
          </button>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">{editingPayment ? 'Edit Payment' : 'Record Payment'}</h3>
                <p className="text-sm text-virgo-teal-400 mt-1">
                  {selectedInvoice.invoice_number} - Balance: {formatCurrency(selectedInvoice.amount_due)}
                </p>
              </div>
              <button
                onClick={closePaymentModal}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleRecordPayment} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Payment Amount *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max={editingPayment ? undefined : selectedInvoice.amount_due}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder="0.00"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Payment Method *
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="check">Check</option>
                  <option value="cash">Cash</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="bank_transfer">Bank Transfer / ACH</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="money_order">Money Order</option>
                  <option value="other">Other</option>
                  <option value="square">Square</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Reference / Check Number
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Check #1234"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  rows={2}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
                  placeholder="Optional payment notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closePaymentModal}
                  className="flex-1 px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingPayment}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submittingPayment ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingPayment ? 'Saving...' : 'Recording...'}
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} className="mr-2" />
                      {editingPayment ? 'Save Changes' : 'Record Payment'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && editInvoice && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Edit Invoice</h3>
                <p className="text-sm text-virgo-teal-400 mt-1">
                  {editInvoice.invoice_number} - {formatCurrency(editInvoice.total_amount)}
                </p>
              </div>
              <button
                onClick={closeEditModal}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleEditInvoice} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-virgo-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Discount Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-virgo-teal-400">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={editDiscount}
                    onChange={(e) => setEditDiscount(e.target.value)}
                    className="w-full pl-8 pr-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-virgo-teal-500"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-1">
                  Notes
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-virgo-teal-500 focus:border-virgo-teal-500 resize-none"
                  placeholder="Invoice notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="flex-1 px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingEdit}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-virgo-teal-600 rounded-lg hover:bg-virgo-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submittingEdit ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Edit2 size={16} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* LEDES Export Modal */}
      {showLedesModal && ledesInvoice && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-cyan-500/30 shadow-2xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-t-xl">
              <div className="flex items-center gap-3">
                <FileCode className="w-6 h-6 text-cyan-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">LEDES E-Bill Export</h3>
                  <p className="text-sm text-virgo-teal-400 mt-0.5">
                    {ledesInvoice.invoice_number} - {formatCurrency(ledesInvoice.total_amount)}
                  </p>
                </div>
              </div>
              <button
                onClick={closeLedesModal}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-virgo-teal-300 mb-3">
                  Export Format
                </label>
                <div className="space-y-3">
                  <label className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all border-2 ${
                    ledesFormat === 'ledes_1998b'
                      ? 'bg-cyan-500/20 border-cyan-500'
                      : 'bg-virgo-navy-600 border-transparent hover:bg-virgo-navy-500'
                  }`}>
                    <input
                      type="radio"
                      name="ledesFormat"
                      value="ledes_1998b"
                      checked={ledesFormat === 'ledes_1998b'}
                      onChange={(e) => setLedesFormat(e.target.value)}
                      className="mt-1 text-cyan-600"
                    />
                    <div>
                      <span className="text-white font-medium">LEDES 1998B</span>
                      <p className="text-sm text-virgo-teal-400 mt-1">
                        Standard pipe-delimited format (.txt). Most widely accepted by insurance companies and corporate clients.
                      </p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all border-2 ${
                    ledesFormat === 'ledes_2000'
                      ? 'bg-cyan-500/20 border-cyan-500'
                      : 'bg-virgo-navy-600 border-transparent hover:bg-virgo-navy-500'
                  }`}>
                    <input
                      type="radio"
                      name="ledesFormat"
                      value="ledes_2000"
                      checked={ledesFormat === 'ledes_2000'}
                      onChange={(e) => setLedesFormat(e.target.value)}
                      className="mt-1 text-cyan-600"
                    />
                    <div>
                      <span className="text-white font-medium">LEDES 2000</span>
                      <p className="text-sm text-virgo-teal-400 mt-1">
                        XML-based format (.xml). Enhanced structure with better data validation and support for complex billing.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="bg-virgo-navy-600/50 rounded-lg p-4 border border-virgo-teal-700/30">
                <h4 className="text-sm font-medium text-virgo-teal-300 mb-2">What is LEDES?</h4>
                <p className="text-xs text-virgo-teal-400">
                  LEDES (Legal Electronic Data Exchange Standard) is the industry standard for electronic legal billing.
                  Corporate clients and insurance companies often require invoices in LEDES format for their e-billing systems.
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeLedesModal}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLedesExport}
                  disabled={exporting}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {exporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download size={16} className="mr-2" />
                      Download LEDES File
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-virgo-navy-600 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-virgo-teal-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {loadingPreview ? 'Loading...' : `Invoice ${previewInvoice?.invoice_number}`}
                  </h3>
                  {previewInvoice && (
                    <p className="text-sm text-virgo-teal-400 mt-0.5">
                      {previewInvoice.contact_name} • {formatDate(previewInvoice.invoice_date)}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={closePreviewModal}
                className="text-virgo-teal-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loadingPreview ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-virgo-teal-400"></div>
                </div>
              ) : previewInvoice ? (
                <div className="space-y-6">
                  {/* Invoice Header Info */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-virgo-navy-600/50 rounded-lg p-4">
                      <h4 className="text-xs font-medium text-virgo-teal-400 uppercase mb-2">Bill To</h4>
                      <p className="text-white font-medium">{previewInvoice.contact_name || 'N/A'}</p>
                      {previewInvoice.case_name && (
                        <p className="text-sm text-virgo-teal-300 mt-1">Case: {previewInvoice.case_name}</p>
                      )}
                    </div>
                    <div className="bg-virgo-navy-600/50 rounded-lg p-4">
                      <h4 className="text-xs font-medium text-virgo-teal-400 uppercase mb-2">Invoice Details</h4>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-virgo-teal-300">Status:</span>
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(previewInvoice.status)}`}>
                            {previewInvoice.status}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-virgo-teal-300">Due Date:</span>
                          <span className="text-white">{formatDate(previewInvoice.due_date)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-virgo-teal-300">Terms:</span>
                          <span className="text-white">{previewInvoice.payment_terms || 'Net 30'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Line Items */}
                  <div>
                    <h4 className="text-sm font-medium text-virgo-teal-300 mb-3">Line Items</h4>
                    <div className="bg-virgo-navy-600/30 rounded-lg overflow-hidden">
                      <table className="min-w-full">
                        <thead className="bg-virgo-navy-600">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Description</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Qty</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Rate</th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Amount</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-virgo-navy-600">
                          {previewInvoice.line_items && previewInvoice.line_items.length > 0 ? (
                            previewInvoice.line_items.map((item, index) => (
                              <tr key={item.id || index}>
                                <td className="px-4 py-3 text-sm text-white">{item.description}</td>
                                <td className="px-4 py-3 text-sm text-virgo-teal-300 text-right">{item.quantity}</td>
                                <td className="px-4 py-3 text-sm text-virgo-teal-300 text-right">{formatCurrency(item.unit_price)}</td>
                                <td className="px-4 py-3 text-sm text-white text-right font-medium">{formatCurrency(item.amount)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="px-4 py-6 text-center text-virgo-teal-400">
                                No line items
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="flex justify-end">
                    <div className="w-64 bg-virgo-navy-600/50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-virgo-teal-300">Subtotal:</span>
                        <span className="text-white">{formatCurrency(previewInvoice.subtotal)}</span>
                      </div>
                      {previewInvoice.discount_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-virgo-teal-300">Discount:</span>
                          <span className="text-red-400">-{formatCurrency(previewInvoice.discount_amount)}</span>
                        </div>
                      )}
                      {previewInvoice.tax_amount > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-virgo-teal-300">Tax ({previewInvoice.tax_rate}%):</span>
                          <span className="text-white">{formatCurrency(previewInvoice.tax_amount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-bold border-t border-virgo-navy-500 pt-2">
                        <span className="text-virgo-teal-200">Total:</span>
                        <span className="text-white">{formatCurrency(previewInvoice.total_amount)}</span>
                      </div>
                      {previewInvoice.amount_paid > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-virgo-teal-300">Paid:</span>
                          <span className="text-green-400">{formatCurrency(previewInvoice.amount_paid)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm font-bold">
                        <span className="text-orange-400">Balance Due:</span>
                        <span className="text-orange-400">{formatCurrency(previewInvoice.amount_due)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payments History */}
                  {previewInvoice.payments && previewInvoice.payments.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-virgo-teal-300 mb-3">Payment History</h4>
                      <div className="bg-virgo-navy-600/30 rounded-lg overflow-hidden">
                        <table className="min-w-full">
                          <thead className="bg-virgo-navy-600">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Method</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-virgo-teal-400 uppercase">Reference</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Amount</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-virgo-teal-400 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-virgo-navy-600">
                            {previewInvoice.payments.map((payment, index) => (
                              <tr key={payment.id || index} className={payment.voided ? 'opacity-50' : ''}>
                                <td className="px-4 py-3 text-sm text-white">{formatDate(payment.payment_date)}</td>
                                <td className="px-4 py-3 text-sm text-virgo-teal-300 capitalize">
                                  {payment.payment_method}
                                  {payment.source === 'square' && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">Square</span>}
                                  {payment.voided && <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-300" title={payment.void_reason || ''}>Voided</span>}
                                </td>
                                <td className="px-4 py-3 text-sm text-virgo-teal-300">{payment.reference_number || '—'}</td>
                                <td className={`px-4 py-3 text-sm text-right font-medium ${payment.voided ? 'line-through text-virgo-teal-500' : 'text-green-400'}`}>{formatCurrency(payment.amount)}</td>
                                <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                                  {!payment.voided && (
                                    <>
                                      {payment.source !== 'square' && (
                                        <button onClick={() => openEditPaymentModal(payment)} className="text-virgo-teal-300 hover:text-white mr-3">Edit</button>
                                      )}
                                      <button onClick={() => handleVoidPayment(payment)} className="text-red-400 hover:text-red-300">Void</button>
                                    </>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {previewInvoice.notes && (
                    <div>
                      <h4 className="text-sm font-medium text-virgo-teal-300 mb-2">Notes</h4>
                      <p className="text-sm text-virgo-teal-400 bg-virgo-navy-600/30 rounded-lg p-3">
                        {previewInvoice.notes}
                      </p>
                    </div>
                  )}

                  {/* Square Payment Link */}
                  {previewInvoice.square_payment_url && (
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-blue-400">Square Payment Link</h4>
                          <p className="text-xs text-blue-300 mt-1">Share this link with your client for online payment</p>
                        </div>
                        <a
                          href={previewInvoice.square_payment_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
                        >
                          <ExternalLink size={16} className="mr-2" />
                          Open Link
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-virgo-navy-600 flex justify-between items-center shrink-0">
              <div>
                {previewInvoice && previewInvoice.status === 'draft' && previewInvoice.amount_due > 0 && (
                  <button
                    onClick={(e) => {
                      closePreviewModal();
                      handleSendViaSquare(previewInvoice, e);
                    }}
                    disabled={sendingSquare === previewInvoice?.id}
                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 transition"
                  >
                    <Send size={16} className="mr-2" />
                    Approve & Send via Square
                  </button>
                )}
              </div>
              <button
                onClick={closePreviewModal}
                className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onInvoiceCreated={(invoice) => {
          fetchInvoices();
          fetchStats();
        }}
      />
      </div>
    </div>
  );
}

export default Invoices;
// Cache bust $(date +%s)
